import React, { useEffect, useRef, useState } from 'react';
import { handleIsLoggingOut, signOut } from '../../features/authSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/customHooks';
import {
  SCreateMessage,
  SIcon,
  SLogo,
  SMarginTop,
  SSectionFour,
  SSectionOne,
  SSectionThree,
  SSectionTwo,
  SWrapper,
} from './styles/NavBar.styled';
import {
  MessageCenterRoute,
  MessageDetailsRoute,
  ReportingRoute,
  TrashBinRoute,
  UserAccountRoute,
  HelpDeskRoute,
  LandingRoute,
  TemplateMaintenanceRoute,
} from '../../common/routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightFromBracket,
  faCalendarRange,
  faClipboard,
  faGlobe,
  faListUl,
  faPlus,
  faSliders,
  faTrashCan,
  faUser,
  faUsers,
  faCircleUser,
  faPhone,
  faFile,
  faTableTree,
} from '@fortawesome/pro-light-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import InstanceChange from './InstanceChange';
import { MenuContainer, MenuListContainer, MenuListItem } from '@/presentation/menu-item-group/MenuItemGroup.styled';
import MenuItemGroup from '@/presentation/menu-item-group/menuItemGroup';
import Text from '@/presentation/text';
import { DirectNetUserType } from '@/api/client';
import { clearMessageSearchResults } from '@/features/messagesSlice';

const basePath = (window as any).CSG_CONFIG.BASE_PATH;

const NavBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authState = useAppSelector((state) => state.auth);
  const [showChangeInstance, setShowChangeInstance] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHelpDesk, setShowHelpDesk] = useState(false);
  const changeInstanceRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const helpDeskRef = useRef<HTMLDivElement>(null);
  const instances = useAppSelector((state) => state.auth.availableInstances);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const userType = useAppSelector((state) => state.auth.usertype);
  const isNativeBC = useAppSelector((state) => state.auth.isNative);
  const [behaviors, setBehaviors] = useState<string[]>();

  useEffect(() => {
    setBehaviors(
      permissions.find((perm) => perm.databaseTarget === instances?.find((inst) => inst.isActive)?.target)?.behaviors,
    );
  }, [instances]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (changeInstanceRef.current && !changeInstanceRef.current.contains(e?.target as Node))
        setShowChangeInstance(false);

      if (helpDeskRef.current && !helpDeskRef.current.contains(e?.target as Node)) setShowHelpDesk(false);
    };

    const handleClickOutsideUserAccount = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e?.target as Node)) setShowUserMenu(false);
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('click', handleClickOutsideUserAccount);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('click', handleClickOutsideUserAccount);
    };
  }, [changeInstanceRef, userMenuRef]);

  const isActive = (route: string) => {
    return location.pathname === route;
  };

  const handleLoggingOut = () => {
    dispatch(handleIsLoggingOut());
    // TODO: make ternary
    if (authState.isLoggingOutWithChanges && location.pathname.includes(`/${basePath}/${MessageDetailsRoute}`)) {
      // TODO: clear values
    } else {
      dispatch(signOut());
    }
  };

  return (
    <SWrapper>
      <SMarginTop />

      <SSectionOne>
        <SLogo
          id="imgBcLogo"
          src={`${process.env.PUBLIC_URL}/assets/images/billComposerCyan.svg `}
          alt="CSG logo"
          className="no-pointer"
        />
      </SSectionOne>

      <SSectionTwo>
        <OverlayTrigger placement="right" overlay={<Tooltip>New Message</Tooltip>}>
          <SCreateMessage
            isActive={isActive(`/${basePath}/${MessageDetailsRoute}`)}
            onClick={() => navigate(`/${basePath}/${MessageDetailsRoute}`)}
            hidden={behaviors?.includes('ReadOnly') && behaviors?.length === 1}
          >
            <FontAwesomeIcon id="menuCreateMessage" icon={faPlus} />
          </SCreateMessage>
        </OverlayTrigger>
      </SSectionTwo>

      <SSectionThree>
        <OverlayTrigger placement="right" overlay={<Tooltip>Landing Page</Tooltip>}>
          <SIcon
            color="dark"
            active={isActive(`/${basePath}/${LandingRoute}`)}
            onClick={() => navigate(`/${basePath}/${LandingRoute}`)}
          >
            <FontAwesomeIcon id="menuDashboard" icon={faCalendarRange} />
          </SIcon>
        </OverlayTrigger>

        <OverlayTrigger placement="right" overlay={<Tooltip>Message Center</Tooltip>}>
          <SIcon
            color="dark"
            active={isActive(`/${basePath}/${MessageCenterRoute}`)}
            onClick={() => {
              dispatch(clearMessageSearchResults());
              navigate(`/${basePath}/${MessageCenterRoute}`);
            }}
          >
            <FontAwesomeIcon id="menuMessageCenter" icon={faListUl} />
          </SIcon>
        </OverlayTrigger>

        <OverlayTrigger placement="right" overlay={<Tooltip>Reports</Tooltip>}>
          <SIcon
            color="dark"
            active={isActive(ReportingRoute)}
            onClick={() => navigate(`/${basePath}/${ReportingRoute}`)}
          >
            <FontAwesomeIcon id="menuReports" icon={faClipboard} />
          </SIcon>
        </OverlayTrigger>
        <OverlayTrigger placement="right" overlay={<Tooltip>Trash Bin</Tooltip>}>
          <SIcon
            color="dark"
            active={isActive(`/${basePath}/${TrashBinRoute}`)}
            onClick={() => {
              navigate(`/${basePath}/${TrashBinRoute}`);
            }}
          >
            <FontAwesomeIcon id="menuTrash" icon={faTrashCan} />
          </SIcon>
        </OverlayTrigger>

        <OverlayTrigger placement="right" overlay={<Tooltip>Admin</Tooltip>}>
          <SIcon color="dark">
            <FontAwesomeIcon id="menuSettings" icon={faSliders} />
          </SIcon>
        </OverlayTrigger>

        <OverlayTrigger placement="right" overlay={<Tooltip>Selectivity Templates</Tooltip>}>
          <SIcon
            color="dark"
            active={isActive(`/${basePath}/${TemplateMaintenanceRoute}`)}
            onClick={() => navigate(`/${basePath}/${TemplateMaintenanceRoute}`)}
          >
            <FontAwesomeIcon id="menuSettings" icon={faTableTree} />
          </SIcon>
        </OverlayTrigger>
      </SSectionThree>

      <SSectionFour>
        <div ref={userMenuRef}>
          {showUserMenu && (
            <MenuContainer>
              <MenuListContainer>
                <MenuItemGroup title="My Account">
                  <MenuListItem
                    className="hoverable--dark"
                    onClick={() => navigate(`/${basePath}/${UserAccountRoute}`)}
                  >
                    <FontAwesomeIcon icon={faCircleUser} />
                    <Text size="xs">Account</Text>
                  </MenuListItem>
                </MenuItemGroup>
              </MenuListContainer>
            </MenuContainer>
          )}
          <OverlayTrigger placement="right" overlay={<Tooltip>Account</Tooltip>}>
            <SIcon color="dark" onClick={() => setShowUserMenu(!showUserMenu)}>
              <FontAwesomeIcon id="menuMyProfile" icon={faUser} />
            </SIcon>
          </OverlayTrigger>
        </div>

        {authState.usertype === DirectNetUserType.Internal && (
          <div ref={changeInstanceRef}>
            {showChangeInstance && (
              <MenuContainer>
                <MenuListContainer style={{ bottom: '50px' }}>
                  <InstanceChange setVisible={(isVisible: boolean) => setShowChangeInstance(isVisible)} />
                </MenuListContainer>
              </MenuContainer>
            )}
            <OverlayTrigger placement="right" overlay={<Tooltip>Change Instance</Tooltip>}>
              <SIcon color="dark" onClick={() => setShowChangeInstance(!showChangeInstance)}>
                <FontAwesomeIcon id="menuChangeInstance" icon={faGlobe} />
              </SIcon>
            </OverlayTrigger>
          </div>
        )}

        <div ref={helpDeskRef}>
          {showHelpDesk && (
            <MenuContainer>
              <MenuListContainer style={{ bottom: '145px' }}>
                <MenuItemGroup title="Help Desk">
                  <MenuListItem className="hoverable--dark" onClick={() => navigate(`/${basePath}/${HelpDeskRoute}`)}>
                    <FontAwesomeIcon id="menuContact" icon={faPhone} />
                    <Text size="xs">Contact</Text>
                  </MenuListItem>
                  <MenuListItem
                    className="hoverable--dark"
                    onClick={() =>
                      window.open('https://www.csgi.com/privacy/privacy-notice', '_blank', 'noopener,noreferrer')
                    }
                  >
                    <FontAwesomeIcon id="menuPrivacy&Terms" icon={faFile} />
                    <Text size="xs">Privacy</Text>
                  </MenuListItem>
                  <a
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    href={
                      userType === 1
                        ? `https://mycsg-docs-prod1.azurewebsites.net/BillComposer/Content/Home1.htm`
                        : `http://flaredocs.csgi.com/onlinedocs/Guides/BillComposer/doclib/GuideWebhelp/Content/Home1.htm`
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MenuListItem className="hoverable--dark">
                      <FontAwesomeIcon id="menuHelp" icon={faFile} />
                      <Text size="xs">Help</Text>
                    </MenuListItem>
                  </a>
                </MenuItemGroup>
              </MenuListContainer>
            </MenuContainer>
          )}
          <OverlayTrigger placement="right" overlay={<Tooltip>Get Help</Tooltip>}>
            <SIcon color="dark" onClick={() => setShowHelpDesk(!showHelpDesk)}>
              <FontAwesomeIcon id="menuGetHelp" icon={faUsers} />
            </SIcon>
          </OverlayTrigger>
        </div>

        {isNativeBC && (
          <OverlayTrigger placement="right" overlay={<Tooltip>Logout</Tooltip>}>
            <SIcon color="dark" onClick={() => handleLoggingOut()}>
              <FontAwesomeIcon id="menuLogout" icon={faArrowRightFromBracket} />
            </SIcon>
          </OverlayTrigger>
        )}
      </SSectionFour>
    </SWrapper>
  );
};

export default NavBar;
